import {AppModals, useAppModalState} from '@src/AppModals'
import {Debug} from '@src/Debug'
import {GLOBALS} from '@src/build-env'
import {DASHBOARD_ROUTES} from '@src/modules/Dashboard/constants'
import {isProd} from '@src/utils/is-prod'
import {AtalayaWrapper} from '@therms/atalaya'
import {
  Alerts,
  AppWrapper2,
  DownloadsApiServiceContextProvider,
  RecordsFieldOptionsConfigProvider,
  resolveFileName,
  RouterContextProvider,
  RPCContextProvider,
  rpcResponseInterceptorLogger,
  useLocalStorage,
} from '@therms/react-modules'
import {RPCClientOptions, TransportType} from '@therms/rpc-client'
import {createBrowserHistory} from 'history'
import {StrictMode, useMemo} from 'react'
import {Router} from 'react-router-dom'
import {AppRouter} from './AppRouter'
import {DownloadsApiClientConfig} from '@therms/downloads-api'

const rpcOptions: RPCClientOptions = {
  hosts: {
    http: GLOBALS.RPC_HTTP_HOST,
    websocket: GLOBALS.RPC_WS_HOST,
  },
  responseInterceptor: rpcResponseInterceptorLogger,
  transportOptions: {
    preferredTransport: TransportType.http,
  },
}

const downloadsApiConfig: DownloadsApiClientConfig = {
  basePath: GLOBALS.BASE_DOWNLOADS_API_URL,
  hooks: {
    onError: (context) => {
      // todo: need to log errors and send to sentry
    },
  },
}

console.log('Build:', GLOBALS.BUILD_DATE, 'RPC HOSTS', rpcOptions.hosts)

export const history = createBrowserHistory({basename: '/ops'})

export function App() {
  // Read the modal-store setters via getState() instead of subscribing the whole
  // App to the store. The setters are stable, so this object is created once and
  // never changes identity — preventing an app-wide re-render of every AppWrapper
  // context consumer (e.g. ActivityFeed) each time a modal opens/closes.
  const appInteractionHandlers = useMemo(() => {
    const {getState} = useAppModalState

    return {
      onClickBolo: (boloId: string) => getState().setShowBoloModal(boloId),
      onClickCheckpointScan: (checkpointId: string) =>
        getState().setShowCheckpointScanModal(checkpointId),
      onClickCheckpointTour: (checkpointId: string) =>
        getState().setShowCheckpointTourModal(checkpointId),
      onClickEvent: (eventId: string) => getState().setShowEventModal(eventId),
      onClickLocation: (locationId: string) =>
        getState().setShowLocationModal(locationId),
      onClickLocationLogEntry: (entryId: string) =>
        getState().setShowLocationLogEntryModal(entryId),
      onClickPassDown: (passDownId: string) =>
        getState().setShowPassDownModal(passDownId),
      onClickPatrolRouteRun: (patrolRouteId: string) =>
        getState().setShowPatrolRouteRunModal(patrolRouteId),
      onClickRecord: (recordId: string) =>
        getState().setShowRecordModal(recordId),
      onClickReport: (reportId: string) =>
        getState().setShowReportModal(reportId),
      onClickShift: (shiftId: string) => getState().setShowShiftModal(shiftId),
      onClickUser: (userId: string) => getState().setShowUserModal(userId),
    }
  }, [])

  return (
    <StrictMode>
      <RPCContextProvider options={rpcOptions}>
        <DownloadsApiServiceContextProvider clientConfig={downloadsApiConfig}>
          <RouterContextProvider history={history}>
            <AtalayaWrapper resolveFileName={resolveFileName}>
              <AppWrapper2
                appInteractionHandlers={appInteractionHandlers}
                currentAppId="ops"
                currentAppVersion={GLOBALS.BUILD_DATE}
                mapboxToken={GLOBALS.MAPBOX_ACCESS_TOKEN}
                onRegionChanged={() => {
                  history.push(DASHBOARD_ROUTES.__ROOT__)

                  Alerts.info(`Region Changed`)
                }}
              >
                <RecordsFieldOptionsConfigProvider>
                  <Router history={history}>
                    <AppRouter />

                    <AppModals />
                  </Router>

                  {!isProd && <Debug />}
                </RecordsFieldOptionsConfigProvider>
              </AppWrapper2>
            </AtalayaWrapper>
          </RouterContextProvider>
        </DownloadsApiServiceContextProvider>
      </RPCContextProvider>
    </StrictMode>
  )
}
